import { Action, Dispatch } from 'redux';
import {
  ACCOUNTS_URL,
  API_DEFAULT_RETRIES,
  HttpStatusCode,
  NAMESPACE_PREFERENCES,
  RequestId,
} from 'appConstants';
import {
  deletePortalPreferences,
  RetryableAction,
  setRequestError,
  setTicketAccount,
} from 'reducers';
import { AccountUtils, ApiRequest, ApiRequestError } from 'services';
import { Account } from 'types';

export interface AccountsAction extends Action, RetryableAction {
  readonly items?: Account[];
}

export interface Accounts {
  readonly items: Account[];
}

export interface AccountsResponse {
  accounts: Account[];
}

/* Initial State */

const initialState = {
  items: [],
} as Accounts;

/* Actions */

enum Actions {
  CLEAR_ACCOUNTS = 'CLEAR_ACCOUNTS',
  SET_ACCOUNTS = 'SET_ACCOUNTS',
}

const clearAccounts = (): AccountsAction => ({
  type: Actions.CLEAR_ACCOUNTS,
});

const setAccounts = (items: Account[]): AccountsAction => ({
  type: Actions.SET_ACCOUNTS,
  items,
});

/* Reducers */

const accountsReducer = (state = initialState, action: AccountsAction) => {
  switch (action.type) {
    case Actions.CLEAR_ACCOUNTS:
      return {
        ...state,
        items: [],
      };
    case Actions.SET_ACCOUNTS:
      return {
        ...state,
        items: action.items,
      };
    default:
      return state;
  }
};

/* Thunks */

const getAccounts = (adminOnly: boolean = false) => (
  dispatch: Dispatch<AccountsAction>,
  getState: Function,
) => {
  const onError = () => dispatch(clearAccounts());

  const onUpdate = (id: string) => {
    const { Requests, accounts, portalPreferences } = getState();
    if (Requests[id].isSuccess && !accounts.items.length) {
      dispatch(
        setRequestError(id, {
          status: HttpStatusCode.FORBIDDEN,
          message: 'observer error',
        } as ApiRequestError),
      );

      if (
        portalPreferences[NAMESPACE_PREFERENCES.CREATE_TICKET_DRAFT] &&
        Object.keys(
          portalPreferences[NAMESPACE_PREFERENCES.CREATE_TICKET_DRAFT],
        ).length > 0
      ) {
        dispatch(
          deletePortalPreferences(NAMESPACE_PREFERENCES.CREATE_TICKET_DRAFT),
        );
      }
    }
  };

  const onSuccess = (accounts: Account[]) => {
    if (accounts.length === 0) {
      dispatch(clearAccounts());
      return;
    }

    dispatch(
      setAccounts(
        adminOnly
          ? [
              ...accounts.filter(AccountUtils.hasAdmin),
              ...accounts.filter(AccountUtils.isDeviceAdmin),
              ...accounts.filter(AccountUtils.isTicketAdmin),
            ]
          : accounts,
      ),
    );

    const accountId = getState().selectedTicket.accountId || '';
    const ticketAccount = AccountUtils.findById(accounts, accountId);

    if (ticketAccount.id) {
      dispatch(setTicketAccount(ticketAccount));
    }
  };

  const transform = (accountResponse: AccountsResponse) =>
    accountResponse.accounts.map((account: Account) => ({
      ...account,
      serviceLevel: account.serviceLevel
        ? account.serviceLevel[0].toUpperCase() + account.serviceLevel.slice(1)
        : '',
      // strip number from id (faws:1234 => 1234)
      number: account.id ? account.id.substr(account.id.indexOf(':') + 1) : '',
    }));

  return ApiRequest.get(ACCOUNTS_URL, {
    dispatchId: RequestId.GET_ACCOUNTS,
    onError,
    onSuccess,
    onUpdate,
    transform,
    retries: API_DEFAULT_RETRIES,
  });
};

export { setAccounts, clearAccounts, accountsReducer, getAccounts };
